import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/private-label", label: "Private Label" },
  { to: "/wholesale", label: "Wholesale" },
  { to: "/sustainability", label: "Sustainability" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    // close mobile menu on route change via microtask to avoid sync setState in effect
    queueMicrotask(() => setOpen(false));
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0A0A09]/80 border-b border-[#F7F5F0]/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link
          to="/"
          data-testid="brand-logo"
          className="flex items-baseline group"
        >
          <span className="font-grotesk text-[#F7F5F0] font-black tracking-tighter text-2xl">
            BLACPAC
          </span>
          <span className="ml-1 inline-block h-2.5 w-2.5 rounded-[2px] bg-[#E85D2F]" />
          <span className="ml-3 hidden sm:inline-block font-grotesk text-[10px] tracking-[0.18em] text-[#F7F5F0]/40 uppercase">
            Packaging the Future
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm tracking-wide transition-colors ${
                  isActive
                    ? "text-[#E85D2F]"
                    : "text-[#F7F5F0]/70 hover:text-[#F7F5F0]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {n.label}
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute left-3 right-3 -bottom-0.5 h-px bg-[#E85D2F]"
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:block">
          <Link
            to="/contact"
            data-testid="header-cta-quote"
            className="rounded-full bg-[#E85D2F] hover:bg-[#C44B25] text-[#F7F5F0] text-sm px-6 py-3 font-medium tracking-wide transition-colors"
          >
            Request a Quote
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen((o) => !o)}
          className="lg:hidden h-10 w-10 rounded-full border border-[#F7F5F0]/15 text-[#F7F5F0] flex items-center justify-center"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden overflow-hidden border-t border-[#F7F5F0]/10 bg-[#0A0A09]/95 backdrop-blur-xl"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {NAV.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 text-lg font-grotesk tracking-tight border-b border-[#F7F5F0]/5 ${
                      isActive ? "text-[#E85D2F]" : "text-[#F7F5F0]"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-cta-quote"
                className="mt-4 rounded-full bg-[#E85D2F] text-center text-[#F7F5F0] px-6 py-4 font-medium"
              >
                Request a Quote
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
